/**
 * @fileoverview 市场相关 API 服务
 * @description 封装市场列表、市场详情、投注时间窗检查等接口
 * MVC 架构：Service 层，负责市场数据 HTTP 通信
 * @module services/marketService
 */

import { get, type BackendResponse } from './api';
import type { Market, MarketCategory, MarketStatus } from '../types';

// ============================================================
// 后端数据类型（原始响应结构）
// ============================================================

/**
 * 后端市场原始数据结构（对应 market 数据库表）
 */
export interface BackendMarket {
  id: number;
  name: string;
  description: string;
  /** 分组类型：crypto/stock/forex/commodity/index/event */
  group: string;
  /**
   * 市场类型：0=高频市场(eRound)，1=事件市场(eEvent)
   * 对应服务器 CLASS_TYPE = { eRound:0, eEvent:1 }
   */
  classType: number;
  /**
   * 标签类型：0=热门(eHot)，1=新上(eNew)
   * 对应服务器 TAG_TYPE = { eHot:0, eNew:1 }
   */
  tagType: number;
  assetSymbol: string;
  logo: string;
  /** 状态：0=过期,1=活跃,2=关闭,3=已结算 */
  status: number;
  /** 结果：0=no, 1=yes */
  result: number;
  /** 价格类型：0=低于目标价,1=等于,2=高于 */
  priceType: number;
  targetPrice: number;
  minDeposit: number;
  maxDeposit: number;
  /** 开始时间（Unix 秒） */
  startTime: number;
  /** 结束时间（Unix 秒） */
  endTime: number;
  /** 每轮时长（高频市场有效） */
  roundTime: number;
  /** 总奖池（最小单位） */
  poolAmount: number;
  /** Yes 方投注总额 */
  yesAmount: number;
  /** No 方投注总额 */
  noAmount: number;
  taxRate: number;
  /** 首页显示状态：0=不显示,>0=显示顺序 */
  showStatus: number;
  isClose: number;
  /** Yes 按钮自定义名称（为空时前端显示默认 "Yes"） */
  yesDesc?: string;
  /** No 按钮自定义名称（为空时前端显示默认 "No"） */
  noDesc?: string;
  /** 参与人数（聚合字段） */
  userCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * 投注时间窗检查 / 当前投注权重响应
 * 对应服务器 GET /api/v1/market/investCheck，返回“此刻下一注能拿到的权重”等信息。
 */
export interface InvestCheckData {
  marketId?: number;
  /** 当前轮次 ID */
  roundId?: number;
  /**
   * 当前下一注能拿到的时间权重（越早押越高）。
   * 用于前端预估“现在押注的返还率”及绘制返还率走势。
   */
  weight?: number;
  /** 权重分段总数（满权重 = slotCount × weightStep） */
  slotCount?: number;
  /** 截止保护起始（Unix 秒），>= 此刻起不可再押 */
  cutoffSec?: number;
  startTime?: number;
  endTime?: number;
  /** 是否可以投注（部分场景返回） */
  canInvest?: boolean;
  /** 不可投注原因 */
  reason?: string;
  /** 距离截止时间剩余秒数 */
  remainSeconds?: number;
}

// ============================================================
// 字段映射工具函数
// ============================================================

/**
 * 将后端 group 字段映射到前端 MarketCategory
 * 直接使用服务器 MARKET_GROUP_TYPE 字段山，无需转换
 * @param group - 后端市场分组
 * @returns 前端市场分类
 */
function mapGroupToCategory(group: string): MarketCategory {
  const validGroups: MarketCategory[] = ['crypto', 'stock', 'forex', 'commodity', 'futures', 'index', 'event'];
  if (validGroups.includes(group as MarketCategory)) {
    return group as MarketCategory;
  }
  // 未知分组默认归到 commodity
  return 'commodity';
}

/**
 * 将后端 status 数字映射到前端 MarketStatus 字符串
 * @param status - 后端状态码
 * @returns 前端市场状态
 */
function mapStatus(status: number, isClose?: number): MarketStatus {
  // isClose=1 明确休市
  if (isClose === 1) return 'closed';
  const mapping: Record<number, MarketStatus> = {
    0: 'cancelled',  // EXPIRED
    1: 'active',     // ACTIVE
    2: 'closed',     // CLOSED (休市)
    3: 'resolved',   // RESOLVED
    4: 'resolved',   // COMPLETE
  };
  return mapping[status] ?? 'active';
}

/**
 * 判断市场是否为近期新上线（7天内创建）
 * @param createdAt - 创建时间字符串
 * @returns 是否为新市场
 */
function isRecentlyCreated(createdAt?: string): boolean {
  if (!createdAt) return false;
  const created = new Date(createdAt).getTime();
  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  return created > sevenDaysAgo;
}

/**
 * 将后端市场数据映射为前端 Market 类型
 * @description 不修改 UI，只在 Service 层做字段转换
 * @param m - 后端市场原始数据
 * @returns 前端 Market 对象
 */
export function mapBackendMarket(m: BackendMarket): Market {
  // 后端字段名与语义一致：
  // m.yesAmount = YES（涨/是）方的累计投注金额
  // m.noAmount  = NO（跌/否）方的累计投注金额
  const yesAmt = Number(m.yesAmount) || 0;
  const noAmt = Number(m.noAmount) || 0;
  const total = yesAmt + noAmt;

  // 计算 Yes/No 百分比，无数据时默认 50/50
  // 赔率 = total / 该方金额（动态分池赔率，在 BetPanel 中用 1/price 计算）
  const yesPercent = total > 0 ? Math.round((yesAmt / total) * 100) : 50;
  const noPercent = total > 0 ? Math.round((noAmt / total) * 100) : 50;

  // 总交易量 = yesAmount + noAmount（单位：USDT，后端直接存储整数金额）
  // poolAmount 是奖池金额，可能为 0（未充入），不用于展示交易量
  const totalAmt = total;
  // 流动性 = poolAmount（奖池）+ 总投注金额，如果 poolAmount 为 0 则用总投注金额代替
  const poolAmt = Number(m.poolAmount) || 0;
  const liquidityAmt = poolAmt > 0 ? poolAmt : totalAmt;

  return {
    id: String(m.id),
    title: m.name,
    description: m.description,
    category: mapGroupToCategory(m.group),
    /**
     * 高频市场判断：classType=0 高频市场，classType=1 普通（低频）市场
     */
    marketType: m.classType === 0 ? 'high_freq' : 'normal',
    status: mapStatus(m.status, m.isClose),
    yesPercent,
    noPercent,
    yesAmount: yesAmt,
    noAmount: noAmt,
    totalVolume: totalAmt,
    totalLiquidity: liquidityAmt,
    // 将 Unix 秒转换为 ISO 字符串；endTime=0 表示高频市场休市，用空字符串标记
    endTime: m.endTime > 0 ? new Date(m.endTime * 1000).toISOString() : '',
    createdAt: m.createdAt || new Date(m.startTime * 1000).toISOString(),
    /**
     * tagType=0 热门市场 (isHot)，tagType=1 新上市场 (isNew)
     * 首页热门区块用 isHot=true 筛选，同时按 totalVolume 降序排列
     */
    isHot: m.tagType === 0,
    isNew: m.tagType === 1,
    // userCount 在 marketList 有，marketInfo 无，兼容处理
    participantCount: m.userCount || 0,
    assetSymbol: m.assetSymbol,
    targetPrice: m.targetPrice,
    /**
     * 高频市场基准价格 = targetPrice（市场创建时的实时价格）
     * currentPrice 由卡片组件从 priceSet 接口实时获取，此处不赋値
     */
    basePrice: m.classType === 0 ? m.targetPrice : undefined,
    currentPrice: undefined,
    coverImage: m.logo || undefined,
    taxRate: Number(m.taxRate) || 0,
    poolAmount: poolAmt,
    yesDesc: m.yesDesc || undefined,
    noDesc: m.noDesc || undefined,
    minDeposit: Number(m.minDeposit) || 0,
    maxDeposit: Number(m.maxDeposit) || 0,
  };
}

// ============================================================
// API 函数
// ============================================================

/**
 * 获取市场列表
 * @param page - 页码，从 0 开始
 * @returns 后端原始市场列表
 */
export async function fetchMarketList(
  page = 0,
  languageCode?: string
): Promise<BackendResponse<BackendMarket[]>> {
  const params: Record<string, string | number> = { page };
  if (languageCode) params.languageCode = languageCode;
  return get<BackendMarket[]>('/api/v1/market/marketList', params);
}

/**
 * 获取市场总数
 * @returns 市场总数
 */
export async function fetchMarketPageCount(): Promise<BackendResponse<number>> {
  return get<number>('/api/v1/market/pageCount');
}

/**
 * 检查指定市场是否可以投注
 * @param marketId - 市场 ID
 * @returns 投注时间窗检查结果
 */
export async function checkInvestWindow(
  marketId: string | number
): Promise<BackendResponse<InvestCheckData>> {
  return get<InvestCheckData>('/api/v1/market/investCheck', { id: Number(marketId) });
}

/**
 * 获取投注锁定时间（截止前多少秒禁止投注）
 * @returns 锁定时间（秒）
 */
export async function fetchInvestLockTime(): Promise<BackendResponse<{ investLockTime: number }>> {
  return get<{ investLockTime: number }>('/api/v1/market/getInvestLockTime');
}

/**
 * 获取代币实时价格
 * @param token - 代币符号（如 "XAU"、"BTC"）
 * @returns 价格数据
 */
export async function fetchTokenPrice(
  token: string
): Promise<BackendResponse<Record<string, number>>> {
  return get<Record<string, number>>('/api/v1/public/tokenPrice', { token });
}

/**
 * 获取市场当前最新价格（closePrice 接口）
 * @param marketId - 市场 ID
 * @param resolution - K 线周期，默认 1m
 * @returns { marketId, group, symbol, price, source }
 */
export interface ClosePriceData {
  marketId: number;
  group: string;
  symbol: string;
  resolution: string;
  price: number | null;
  source: string;
  cache?: {
    redisKey: string;
    symbol: string;  // sync_assets 表中的真实 symbol，用于 WS subscribe
    session: string;
    openTime: number;
  };
}

export async function fetchClosePrice(
  marketId: string | number,
  resolution = '1m'
): Promise<BackendResponse<ClosePriceData>> {
  const params: Record<string, string | number> = { marketId: Number(marketId), resolution };
  return get<ClosePriceData>(
    '/api/v1/public/closePrice',
    params
  );
}

/**
 * 获取单个市场详情（新接口，比 marketList 更高效）
 * @param marketId - 市场 ID
 * @returns 后端原始市场数据
 */
export async function fetchMarketInfo(
  marketId: string | number,
  languageCode?: string
): Promise<BackendResponse<BackendMarket>> {
  const params: Record<string, string | number> = { marketId: Number(marketId) };
  if (languageCode) params.languageCode = languageCode;
  return get<BackendMarket>('/api/v1/market/marketInfo', params);
}

// ============================================================
// 交易时段状态接口
// ============================================================

/**
 * sessionOpen 接口返回结构
 */
export interface SessionOpenResult {
  /** 市场是否处于开市状态 */
  open: boolean;
  /** 市场代码（US/HK/CN 等） */
  market: string;
  /** 时区 */
  timeZone: string;
  /** 当前本地日期 YYYYMMDD */
  localDay: string;
  /** 查询时间戳（Unix 秒） */
  atSec: number;
  /** 是否在交易日列表中 */
  inTradingDayList: boolean;
  /** 是否半日交易日 */
  isHalfDay: boolean;
  /** 匹配的交易时段（open=true 时有值） */
  matchedSession: {
    type: string;
    begin_time: string;
    end_time: string;
    beginTime: number;
    endTime: number;
  } | null;
  /** 关闭原因（open=false 时有值） */
  reason?: string;
  /**
   * 时段开始时间（Unix 秒）
   * - open=false：下次开市时间
   * - open=true：本时段开始时间
   */
  beginTime: number;
  /**
   * 时段结束时间（Unix 秒）
   * - open=true：本时段结束时间（本轮结束时间）
   * - open=false：最近时段结束时间
   */
  endTime: number;
  /** 本地时间格式的开始时间 */
  beginTimeLocal: string;
  /** 本地时间格式的结束时间 */
  endTimeLocal: string;
  /** 窗口类型：matched / nearest */
  windowType: string;
}

/**
 * 查询市场交易时段状态
 * GET /api/v1/market/sessionOpen?marketId=xxx
 * @param marketId - 市场 ID
 * @returns 开市状态及时间窗口信息
 */
export async function fetchSessionOpen(
  marketId: string | number
): Promise<BackendResponse<SessionOpenResult>> {
  return get<SessionOpenResult>('/api/v1/market/sessionOpen', { marketId: Number(marketId) });
}

/**
 * 获取全量价格集合
 * @returns 所有代币价格
 */
export async function fetchPriceSet(): Promise<BackendResponse<Record<string, number>>> {
  return get<Record<string, number>>('/api/v1/public/priceSet');
}

// ============================================================
// K 线数据接口
// ============================================================

export interface KlineResponse {
  ok: boolean;
  symbol: string;
  resolution: string;
  session: string;
  count: number;
  candles: Array<{
    openTime: number;
    closeTime?: number;
    open: string | number;
    high: string | number;
    low: string | number;
    close: string | number;
    volume?: string | number;
  }>;
}

/**
 * 获取 K 线历史数据（通过 data-server /api/kline/latest）
 * data-server kline 接口无需认证，直接请求即可
 * @param symbol - 资产符号，如 "BTCUSDT"、"XAUUSDT"
 * @param resolution - 周期，新版服务器存储秒 K，传 "1s"；图表层自行聚合
 * @param limit - 返回条数，默认 200，最大 2000
 * @returns K 线数据
 */
export async function fetchKlineData(
  symbol: string,
  resolution: string = '1s',
  limit: number = 200
): Promise<KlineResponse | null> {
  try {
    const sym = symbol.toUpperCase().replace('/', '');
    const url = `/api/v1/kline/latest?symbol=${encodeURIComponent(sym)}&resolution=${encodeURIComponent(resolution)}&limit=${limit}`;
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const data = await resp.json();
    return data as KlineResponse;
  } catch {
    return null;
  }
}

/**
 * 预热 K 线缓存（调用 data-server /api/v1/kline/prime）
 * 触发 data-server → sync-server 重建完整历史 legacy list
 * 必须在 WS subscribe 之前调用，否则 kline_snapshot 只有少量实时数据
 * data-server kline 接口无需认证，直接请求即可
 * @param symbol - 资产符号，如 "BTCUSDT"
 * @param resolution - 周期，如 "1m"
 * @param lookbackDays - 回溯天数，默认 7
 * @param limit - 最大条数，默认 500
 */
export async function primeKlineCache(
  symbol: string,
  resolution: string = '1m',
  lookbackDays: number = 7,
  limit: number = 500
): Promise<void> {
  try {
    const sym = symbol.toUpperCase().replace('/', '');
    const url = `/api/v1/kline/prime?symbol=${encodeURIComponent(sym)}&resolution=${encodeURIComponent(resolution)}&lookbackDays=${lookbackDays}&limit=${limit}&onlyEmpty=false`;
    await fetch(url);
  } catch {
    // prime 失败不阻断主流程，WS subscribe 仍然继续
  }
}

/**
 * 将资产符号转换为 K 线查询 symbol（与 sync_assets 表中的 symbol 完全一致）
 * 根据 closePriceSet 接口确认的格式：
 * - stock 组（大宗商品 ETF）：直接用 assetSymbol，保留 .US 后缀（GLD.US, SLV.US）
 * - crypto 组：assetSymbol + USDT（BTC → BTCUSDT）
 * - futures 组：直接用 assetSymbol（MA1!, TA1!）
 * - index 组：直接用 assetSymbol（UUP.US 等）
 */
export function toKlineSymbol(assetSymbol: string, group: string): string {
  if (!assetSymbol) return '';
  // stock 组（大宗商品 ETF）：直接用 assetSymbol，保留 .US 后缀
  if (group === 'stock' || group === 'index') {
    return assetSymbol.toUpperCase();
  }
  // 移除斜杠，统一大写
  let s = assetSymbol.toUpperCase().replace('/', '').replace('-', '');
  // crypto 组：确保以 USDT 结尾（BTC → BTCUSDT）
  if (group === 'crypto') {
    if (!s.endsWith('USDT') && !s.endsWith('BUSD') && !(s.length > 3 && s.endsWith('BTC'))) {
      s = s + 'USDT';
    }
    return s;
  }
  // futures 组：直接用 assetSymbol（MA1!, TA1! 等）
  if (group === 'futures') {
    return s;
  }
  // commodity 组（旧版大宗商品）：XAU → XAUUSDT
  if (group === 'commodity') {
    if (!s.endsWith('USDT')) s = s + 'USDT';
    return s;
  }
  // 其他：直接返回
  return s;
}

/**
 * 根据市场 group 返回服务器存储的 K 线 resolution
 * - crypto（binance）：服务器存储 1s 高频数据，但前端统一使用 1m 以获取更长时间跨度的历史
 *   （1s 数据只保留最近 1 小时，600 条 1s K 线仅 10 分钟；1m 数据保留 24+ 小时）
 * - stock/futures/index（infoway）：存储 1m 数据
 */
export function toKlineResolution(group: string): string {
  // 所有市场统一使用 1m resolution，获取更长时间跨度的历史数据
  return '1m';
}

// ============================================================
// 行情跑马灯接口
// ============================================================

/**
 * closePriceSet 接口返回的单个资产行情数据
 */
export interface ClosePriceItem {
  id: number;
  provider: string;
  group: string;
  symbol: string;
  resolution: string;
  latestPrice: number;
  prevClosePrice: number;
  changePercent: number;
  source: string;
}

/**
 * closePriceSet 接口返回结构
 */
export interface ClosePriceSetData {
  ts: number;
  count: number;
  dedupBySymbol: boolean;
  sourceAssetCount: number;
  dedupAssetCount: number;
  items: ClosePriceItem[];
  fromCache?: boolean;
}

/**
 * 获取全量资产最新价与涨跌幅（用于行情跑马灯）
 * 服务端缓存 5 分钟，前端建议每 30 秒轮询一次
 */
export async function fetchClosePriceSet(): Promise<BackendResponse<ClosePriceSetData>> {
  return get<ClosePriceSetData>('/api/v1/public/closePriceSet');
}
